import React from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  MapPin,
  Clock,
  ArrowUpRight,
  Heart,
  Coffee,
  TrendingUp,
  Globe,
  Sparkles,
  Mail,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

/* ------------------------------------------------------------------ */
/*  CONTENT                                                            */
/* ------------------------------------------------------------------ */
const openings = [
  { id: "ENG-01", title: "Senior Full-Stack Engineer",   team: "Engineering",  location: "Hyderabad · IN",  type: "Full-time", accent: C.coral },
  { id: "ENG-02", title: "AI/ML Engineer",                team: "AI Platform",  location: "Remote · APAC",   type: "Full-time", accent: C.mint  },
  { id: "DSN-01", title: "Senior Product Designer",       team: "Design",       location: "Hybrid · IN/UK",  type: "Full-time", accent: C.gold  },
  { id: "PM-01",  title: "Product Manager · Recruit",     team: "Product",      location: "Remote · global", type: "Full-time", accent: C.coral },
  { id: "CS-01",  title: "Customer Success Manager",      team: "Success",      location: "Bradford · UK",   type: "Full-time", accent: C.mint  },
  { id: "GTM-01", title: "Enterprise Account Executive",  team: "Sales",        location: "Jersey City · US",type: "Full-time", accent: C.gold  },
];

const benefits = [
  { icon: Heart,       title: "Health & wellbeing", desc: "Premium medical, mental health support, fitness stipend." },
  { icon: Globe,       title: "Remote-friendly",     desc: "Hybrid by default; fully remote roles where the work allows." },
  { icon: TrendingUp,  title: "Real equity",         desc: "Every full-timer gets meaningful stock options." },
  { icon: Coffee,      title: "Time off, properly",  desc: "Unlimited PTO + a mandatory 2-week annual reset." },
];

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
export default function Careers() {
  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight"
        style={{ borderColor: C.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Careers · {openings.length} open roles</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] xl:text-[5.75rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Build hiring's
              <br />
              <span style={{ color: `${C.forest}80` }}>quiet upgrade.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              We're a small, remote-friendly team across India, UK and the US.
              We ship calmly, treat candidates well and write code we'd want to
              read on Monday.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* BENEFITS */}
      <section className="py-16 md:py-20 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-10 max-w-2xl">
            <StatusBadge>The deal</StatusBadge>
            <h2
              className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight"
              style={{ color: C.forest }}
            >
              What you get.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: C.hairline }}>
            {benefits.map((b, i) => (
              <div key={b.title} className="p-7 md:p-8" style={{ background: C.paper }}>
                <div
                  className="w-11 h-11 flex items-center justify-center mb-5"
                  style={{ background: C.forest, color: C.paper }}
                >
                  <b.icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <h3 className="mt-1 text-lg font-bold leading-tight" style={{ color: C.forest }}>
                  {b.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.grid }}>
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* OPENINGS */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>Open roles</StatusBadge>
              <h2
                className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight"
                style={{ color: C.forest }}
              >
                Currently hiring.
              </h2>
            </div>
            <TechLabel style={{ color: `${C.grid}99` }}>
              {openings.length} positions · updated this week
            </TechLabel>
          </div>

          <div className="border" style={{ borderColor: C.hairline, background: C.paper }}>
            {openings.map((role, i) => (
              <motion.a
                key={role.id}
                href="mailto:Info@webidealglobal.com?subject=Application: "
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="group block p-5 md:p-6 transition-colors duration-200 hover:bg-[#1A3C2B]/[0.025]"
                style={{
                  borderTop: i === 0 ? "none" : `1px solid ${C.hairline}`,
                  borderLeft: `4px solid ${role.accent}`,
                }}
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    <TechLabel className="mt-1.5 shrink-0" style={{ color: role.accent }}>
                      {role.id}
                    </TechLabel>
                    <div className="min-w-0">
                      <h3
                        className="text-lg md:text-xl font-bold leading-tight transition-colors group-hover:text-[#1A3C2B]"
                        style={{ color: C.forest }}
                      >
                        {role.title}
                      </h3>
                      <div
                        className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
                        style={{ color: `${C.grid}cc` }}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <Briefcase className="w-3 h-3" />
                          {role.team}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="w-3 h-3" />
                          {role.location}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          {role.type}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span
                    className="w-9 h-9 flex-shrink-0 flex items-center justify-center border transition-all duration-200 group-hover:bg-[#1A3C2B] group-hover:border-[#1A3C2B] group-hover:text-[#F7F7F5]"
                    style={{ borderColor: C.hairline, color: C.grid }}
                  >
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>

          <div className="mt-8 text-center">
            <TechLabel style={{ color: `${C.grid}99` }}>
              Don't see your role? · Email us anyway · we keep great candidates on file
            </TechLabel>
          </div>
        </PageContainer>
      </section>

      {/* SPONTANEOUS APPLY */}
      <section className="py-20 md:py-24" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div
            className="relative border p-10 md:p-14"
            style={{ borderColor: "rgba(158,255,191,0.25)" }}
          >
            <CornerMarkers color={C.mint} />

            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <StatusBadge color={C.mint}>Spontaneous apply</StatusBadge>
                <h2 className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
                  Don't see your role?
                  <br />
                  <span style={{ color: C.mint }}>We're still listening.</span>
                </h2>
                <p className="mt-4 text-base md:text-lg max-w-xl" style={{ color: "rgba(247,247,245,0.75)" }}>
                  We hire people, not job descriptions. Send what you've made and
                  what you'd want to make next.
                </p>
              </div>
              <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
                <a href="mailto:Info@webidealglobal.com?subject=Spontaneous application">
                  <button
                    type="button"
                    className="ds-skeu-btn-mint cursor-pointer h-12 px-7 text-sm font-semibold inline-flex items-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email us
                  </button>
                </a>
                <Link to={createPageUrl("About")}>
                  <button
                    type="button"
                    className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#9EFFBF] hover:text-[#1A3C2B] transition-colors duration-150"
                    style={{ borderColor: "rgba(158,255,191,0.4)", color: C.paper }}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    About us
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
